import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Building2, 
  Users, 
  HardDrive, 
  Activity, 
  DollarSign,
  Calendar,
  Mail,
  Phone,
  Globe,
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Edit,
  Trash2,
  Pause,
  Play,
  BarChart3
} from 'lucide-react';
import { api } from '../auth/authStore';

interface TenantDetail {
  id: string;
  name: string;
  subdomain: string;
  customDomain?: string;
  plan: string;
  status: string;
  billing: {
    status: string;
    trialEnd?: string;
    totalRevenue: number;
    currency: string;
    cancelAtPeriodEnd: boolean;
  };
  usage: {
    users: { current: number; limit: number };
    storage: { current: number; limit: number };
    apiCalls: { current: number; limit: number };
    features: string[];
  };
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    company: string;
  };
  config: {
    timezone: string;
    locale: string;
    currency: string;
    ssoEnabled: boolean;
    mfaRequired: boolean;
    passwordPolicy: {
      minLength: number;
      requireUppercase: boolean;
      requireLowercase: boolean;
      requireNumbers: boolean;
      requireSymbols: boolean;
      maxAge: number;
    };
  };
  metadata: {
    source?: string;
    notes?: string;
    tags?: string[];
  };
  trialStartedAt?: string;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
}

export const TenantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (id) {
      fetchTenantDetail(id);
    }
  }, [id]);

  const fetchTenantDetail = async (tenantId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/platform/tenants/${tenantId}`);

      if (response.data.success) {
        setTenant(response.data.data);
      } else {
        console.error('Failed to fetch tenant detail:', response.data.message);
      }
    } catch (error: any) {
      console.error('Failed to fetch tenant detail:', error);
      // Handle specific error cases
      if (error.response?.status === 404) {
        console.error('Tenant not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'TRIAL':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'SUSPENDED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'TRIAL':
        return 'bg-yellow-100 text-yellow-800';
      case 'SUSPENDED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatUsagePercentage = (current: number, limit: number) => {
    return Math.round((current / limit) * 100);
  };

  const getUsageColor = (current: number, limit: number) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const handleSuspendTenant = async () => {
    if (!tenant || !confirm('Are you sure you want to suspend this firm?')) return;

    try {
      const response = await api.post(`/platform/tenants/${tenant.id}/suspend`, {
        reason: 'Suspended via platform admin'
      });

      if (response.data.success) {
        fetchTenantDetail(tenant.id);
      }
    } catch (error) {
      console.error('Failed to suspend tenant:', error);
    }
  };

  const handleReactivateTenant = async () => {
    if (!tenant || !confirm('Are you sure you want to reactivate this firm?')) return;

    try {
      const response = await api.post(`/platform/tenants/${tenant.id}/reactivate`);

      if (response.data.success) {
        fetchTenantDetail(tenant.id);
      }
    } catch (error) {
      console.error('Failed to reactivate tenant:', error);
    }
  };

  const handleEditTenant = () => {
    if (!tenant) return;
    navigate(`/platform/tenants/${tenant.id}/edit`);
  };

  const handleDeleteTenant = async () => {
    if (!tenant || !confirm('Are you sure you want to delete this firm? This action cannot be undone.')) return;

    try {
      const response = await api.delete(`/platform/tenants/${tenant.id}`);
      if (response.data.success) {
        alert('Firm deleted successfully!');
        navigate('/platform/tenants'); // Redirect to firm list
      } else {
        console.error('Failed to delete firm:', response.data.message);
        alert('Failed to delete firm: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Failed to delete firm:', error);
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to delete firm';
      alert('Error: ' + errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading firm details...</p>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Firm Not Found</h2>
          <p className="text-gray-600 mb-4">The requested firm could not be found.</p>
          <button 
            onClick={() => navigate('/platform/tenants')}
            className="btn-primary"
          >
            Back to Firms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/platform/tenants')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mr-4">
                  <Building2 className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
                  <p className="text-gray-600">{tenant.subdomain}.erpca.ca</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                {getStatusIcon(tenant.status)}
                <span className={`ml-2 px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(tenant.status)}`}>
                  {tenant.status}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleEditTenant}
                  className="btn-secondary"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </button>

                {tenant.status === 'SUSPENDED' ? (
                  <button
                    onClick={handleReactivateTenant}
                    className="btn-success"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Reactivate
                  </button>
                ) : (
                  <button
                    onClick={handleSuspendTenant}
                    className="btn-warning"
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    Suspend
                  </button>
                )}

                <button
                  onClick={handleDeleteTenant}
                  className="btn-danger"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="border-t border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'overview', name: 'Overview' },
                { id: 'usage', name: 'Usage & Billing' },
                { id: 'settings', name: 'Settings' },
                { id: 'analytics', name: 'Analytics' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                    <p className="text-sm text-gray-900">{tenant.contact.company}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                    <p className="text-sm text-gray-900">{tenant.plan}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain</label>
                    <p className="text-sm text-gray-900">{tenant.subdomain}.erpca.ca</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Domain</label>
                    <p className="text-sm text-gray-900">{tenant.customDomain || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                    <p className="text-sm text-gray-900">{new Date(tenant.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Activity</label>
                    <p className="text-sm text-gray-900">{new Date(tenant.lastActivityAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {tenant.contact.firstName} {tenant.contact.lastName}
                      </p>
                      <p className="text-sm text-gray-600">{tenant.contact.email}</p>
                    </div>
                  </div>
                  {tenant.contact.phone && (
                    <div className="flex items-center">
                      <Phone className="w-5 h-5 text-gray-400 mr-3" />
                      <p className="text-sm text-gray-900">{tenant.contact.phone}</p>
                    </div>
                  )}
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 text-gray-400 mr-3" />
                    <p className="text-sm text-gray-900">{tenant.contact.company}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {tenant.metadata.notes && (
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
                  <p className="text-sm text-gray-700">{tenant.metadata.notes}</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Usage Overview */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Overview</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Users</span>
                      </div>
                      <span className="text-sm text-gray-900">
                        {tenant.usage?.users?.current || 0} / {tenant.usage?.users?.limit || 0}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getUsageColor(tenant.usage?.users?.current || 0, tenant.usage?.users?.limit || 0)}`}
                        style={{ width: `${Math.min(100, formatUsagePercentage(tenant.usage?.users?.current || 0, tenant.usage?.users?.limit || 0))}%` }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <HardDrive className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Storage</span>
                      </div>
                      <span className="text-sm text-gray-900">
                        {((tenant.usage?.storage?.current || 0) / 1024).toFixed(1)}GB / {((tenant.usage?.storage?.limit || 0) / 1024).toFixed(1)}GB
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getUsageColor(tenant.usage?.storage?.current || 0, tenant.usage?.storage?.limit || 0)}`}
                        style={{ width: `${Math.min(100, formatUsagePercentage(tenant.usage?.storage?.current || 0, tenant.usage?.storage?.limit || 0))}%` }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <Activity className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-700">API Calls</span>
                      </div>
                      <span className="text-sm text-gray-900">
                        {(tenant.usage?.apiCalls?.current || 0).toLocaleString()} / {(tenant.usage?.apiCalls?.limit || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getUsageColor(tenant.usage?.apiCalls?.current || 0, tenant.usage?.apiCalls?.limit || 0)}`}
                        style={{ width: `${Math.min(100, formatUsagePercentage(tenant.usage?.apiCalls?.current || 0, tenant.usage?.apiCalls?.limit || 0))}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Information */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(tenant.billing?.status || 'UNKNOWN')}`}>
                      {tenant.billing?.status || 'UNKNOWN'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Revenue</span>
                    <span className="text-sm font-medium text-gray-900">
                      ${(tenant.billing?.totalRevenue || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Currency</span>
                    <span className="text-sm font-medium text-gray-900">
                      {tenant.billing?.currency || 'USD'}
                    </span>
                  </div>
                  {tenant.billing?.trialEnd && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Trial Ends</span>
                      <span className="text-sm font-medium text-gray-900">
                        {new Date(tenant.billing.trialEnd).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
                    <BarChart3 className="w-4 h-4 inline mr-2" />
                    View Analytics
                  </button>
                  <button
                    onClick={() => navigate(`/platform/tenants/${tenant.id}/settings`)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                  >
                    <Settings className="w-4 h-4 inline mr-2" />
                    Manage Settings
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
                    <DollarSign className="w-4 h-4 inline mr-2" />
                    Change Plan
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage & Billing Details</h3>
            <p className="text-gray-600">Detailed usage and billing information will be displayed here.</p>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tenant Settings</h3>
            <p className="text-gray-600">Tenant configuration and settings will be displayed here.</p>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Analytics</h3>
            <p className="text-gray-600">Detailed analytics and metrics will be displayed here.</p>
          </div>
        )}
      </div>
    </div>
  );
};
